import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  parseProgramProgress,
  parseProgramsList,
  splitProgramsByStatus,
  type Program,
  type ProgramAssigneesPatch,
  type ProgramProgress,
  type ProgramStatus,
} from "@heraion/shared";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { writeError, type DurableWriteFn } from "../seams";

// ONE programs-list data layer for every surface (anti-monster S-B):
// mobile's Plan tab and web-rnw's console list both build on this hook,
// so a fetch/parse/mutation fix lands once. The host injects apiFetch
// and (optionally) platform cache adapters; confirm dialogs and error
// alerts stay host-side — this hook reports, it never renders.
//
// Semantics preserved from the mobile original (useProgramsData):
//   - programs: cache-first hydrate, then network refresh (SWR).
//   - progress (PROGPROG markers, opt-in): fetched per runnable id;
//     re-fetched when the runnable id SET changes, per-id errors → null.
//   - refreshSignal: parent-driven tick (pull-to-refresh, AppState,
//     focus) that re-kicks the programs fetch.
//   - mutations re-fetch the list even on failure and RETURN the error
//     (null on success) so each host renders its own failure UX.

export type ProgramsResourceStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "refreshing"
  | "error";

/** Platform cache adapter (AsyncStorage on mobile; web-rnw passes
 *  none). Structurally identical to mobile's ResourceCache. */
export type ProgramsCache<T> = {
  read: () => Promise<T | null>;
  write: (value: T) => Promise<void>;
};

export type ProgressByProgram = Record<string, ProgramProgress | null>;

export type UseProgramsOptions = {
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** Fetch the per-program PROGPROG progress markers (the athlete run
   *  surface needs them; the console list doesn't). Default false. */
  withProgress?: boolean;
  programsCache?: ProgramsCache<Program[]>;
  progressCache?: ProgramsCache<ProgressByProgram>;
  /** Bump to re-kick the programs fetch (pull-to-refresh, focus…). */
  refreshSignal?: number;
  /** Ids the caller FOLLOWS (useFollows). Their bucket is decided by the
   *  follow, not by the author's status. A host that doesn't follow
   *  anything, or hasn't been taught about follows, omits it and buckets
   *  exactly as before. */
  followedProgramIds?: ReadonlySet<string>;
  /** False: no fetch at all, the list stays null (#336: the program editor
   *  needs it on the phone only). Default true. */
  enabled?: boolean;
};

export function usePrograms(opts: UseProgramsOptions) {
  const { withProgress = false, refreshSignal } = opts;
  const [programs, setPrograms] = useState<Program[] | null>(null);
  const [programsStatus, setProgramsStatus] =
    useState<ProgramsResourceStatus>("idle");
  const [programsError, setProgramsError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<ProgressByProgram | null>(null);
  const [progressStatus, setProgressStatus] =
    useState<ProgramsResourceStatus>("idle");
  // Latest progress, for the refetch merge below (preserve a known marker
  // when a per-id fetch transiently fails — see the effect).
  const progressRef = useRef<ProgressByProgram | null>(null);
  progressRef.current = progress;

  // apiFetch + caches via refs: hosts may pass fresh literals per render
  // and none of the effects below should re-run for that (the unstable-
  // object-dep refetch loop, the hard way — see useAsyncResource).
  const apiRef = useRef(opts.apiFetch);
  const writeRef = useRef(opts.durableWrite);
  const programsCacheRef = useRef(opts.programsCache);
  const progressCacheRef = useRef(opts.progressCache);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    writeRef.current = opts.durableWrite;
    programsCacheRef.current = opts.programsCache;
    progressCacheRef.current = opts.progressCache;
  });

  const refreshPrograms = useCallback(async (): Promise<void> => {
    setProgramsStatus((prev) => (prev === "loaded" ? "refreshing" : "loading"));
    setProgramsError(null);
    try {
      const raw = await apiRef.current<unknown>("/api/me/programs");
      const parsed = parseProgramsList(raw);
      setPrograms(parsed);
      setProgramsStatus("loaded");
      void programsCacheRef.current?.write(parsed);
    } catch (e) {
      setProgramsStatus("error");
      setProgramsError(e instanceof Error ? e : new Error(String(e)));
    }
  }, []);

  const enabled = opts.enabled ?? true;
  // Mount: hydrate from the platform cache (if any), then refresh.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      const cached = await programsCacheRef.current?.read();
      if (cancelled) return;
      if (cached != null) {
        setPrograms(cached);
        setProgramsStatus("loaded");
      }
      void refreshPrograms();
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshPrograms, enabled]);

  // Parent-driven refresh tick. Skips the initial value — the mount
  // effect already fetched.
  useEffect(() => {
    if (!enabled || refreshSignal === undefined || refreshSignal === 0) return;
    void refreshPrograms();
  }, [refreshSignal, refreshPrograms, enabled]);

  const {
    followed: followedPrograms,
    published: publishedPrograms,
    drafts: draftPrograms,
  } = useMemo(
    () => splitProgramsByStatus(programs ?? [], opts.followedProgramIds),
    [programs, opts.followedProgramIds],
  );

  // Progress is keyed by the SORTED followed id set: a changed set
  // (created/deleted/followed program) re-fetches; a same-set list
  // refresh doesn't (mirrors the original mobile keying). Only followed
  // programs show "next up" surfaces, so they're the only ones whose
  // PROGPROG markers are worth fetching.
  const idsKey = useMemo(
    () =>
      followedPrograms
        .map((p) => p.id)
        .sort()
        .join(","),
    [followedPrograms],
  );
  const idsRef = useRef<string[]>([]);
  idsRef.current = followedPrograms.map((p) => p.id).sort();
  const progressHydratedRef = useRef(false);

  useEffect(() => {
    if (!withProgress) return;
    let cancelled = false;
    void (async () => {
      if (!progressHydratedRef.current) {
        progressHydratedRef.current = true;
        const cached = await progressCacheRef.current?.read();
        if (!cancelled && cached != null) {
          setProgress(cached);
          setProgressStatus("loaded");
        }
      }
      const ids = idsRef.current;
      if (cancelled || ids.length === 0) return;
      setProgressStatus((prev) =>
        prev === "loaded" ? "refreshing" : "loading",
      );
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const raw = await apiRef.current<unknown>(
              `/api/me/programs/${encodeURIComponent(id)}/progress`,
            );
            return { id, value: parseProgramProgress(raw), ok: true };
          } catch {
            return { id, value: null as ProgramProgress | null, ok: false };
          }
        }),
      );
      if (cancelled) return;
      // Merge, don't replace: a per-id fetch that FAILED must not wipe a
      // previously-known marker to null — that made a recently-trained
      // program look never-run and reordered the Plan carousel (web has no
      // progress cache to backstop it, so a single transient failure flipped
      // the lead card; Antenore 2026-06-18). A SUCCESSFUL null is genuine
      // (never started) and is kept.
      const prev = progressRef.current;
      const map: ProgressByProgram = {};
      for (const e of entries) {
        map[e.id] = e.ok ? e.value : (prev?.[e.id] ?? null);
      }
      setProgress(map);
      setProgressStatus("loaded");
      void progressCacheRef.current?.write(map);
    })();
    return () => {
      cancelled = true;
    };
    // refreshSignal is in the deps so the parent-driven tick (pull-to-
    // refresh, AppState 'active', Plan-tab focus) re-fetches the PROGPROG
    // markers too — NOT just the programs list. Without it, completing a
    // program session advanced the server marker but the client kept the
    // stale pointer (idsKey, the followed-id SET, hasn't changed), so the
    // card recomputed the SAME "next up" and the cycle never rolled over
    // (e.g. finish D5 of a 5-day program, it stays on D5 instead of
    // restarting at D1 — Antenore 2026-06-29, inbox 305e961c). The merge
    // above keeps the SWR semantics (no flash, no carousel reorder).
  }, [withProgress, idsKey, refreshSignal]);

  // Mutations re-fetch whatever the outcome (the server may have partially
  // moved) and hand the error back instead of rendering it. A queued write
  // returns null: durable on mobile, so not an error (DX-2/F).
  const deleteProgram = useCallback(
    async (id: string): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "DELETE",
        path: `/api/me/programs/${encodeURIComponent(id)}`,
      });
      void refreshPrograms();
      return writeError(result);
    },
    [refreshPrograms],
  );

  // Status moves cover BOTH axes: follow (active↔archived) AND lifecycle
  // (draft↔archived, i.e. publish / un-publish) — the server's status-only
  // endpoint already validates against the full ProgramStatus enum.
  const setProgramStatus = useCallback(
    async (id: string, status: ProgramStatus): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "PATCH",
        path: `/api/me/programs/${encodeURIComponent(id)}/status`,
        body: { status },
      });
      void refreshPrograms();
      return writeError(result);
    },
    [refreshPrograms],
  );

  // CO-21 — the assignees-only partial (packages/shared's twin of the
  // status-only PATCH above): the standalone Assign sheet on a Library card
  // saves just `assignedTo`/`assigneeEmails`, never the whole program, so it
  // doesn't need the full editor's draft/build machinery. Same shape as
  // setProgramStatus: PATCH, re-fetch regardless of outcome, hand the error
  // back instead of rendering it.
  const setProgramAssignees = useCallback(
    async (id: string, body: ProgramAssigneesPatch): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "PATCH",
        path: `/api/me/programs/${encodeURIComponent(id)}/assignees`,
        body,
      });
      void refreshPrograms();
      return writeError(result);
    },
    [refreshPrograms],
  );

  return {
    programs: programs ?? [],
    followedPrograms,
    publishedPrograms,
    draftPrograms,
    progress,
    programsStatus,
    progressStatus,
    programsError,
    refreshPrograms,
    deleteProgram,
    setProgramStatus,
    setProgramAssignees,
  };
}

export type ProgramsState = ReturnType<typeof usePrograms>;
